import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  ButtonBase,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import type {
  FunnelBucket,
  FunnelDeal,
  FunnelDrillStage,
  FunnelGroupBy,
  FunnelMetric,
  FunnelOutcomes,
  FunnelPeriod,
  FunnelStages,
  FunnelSummaryResponse,
} from "../../../types/dealFunnel";
import { apiGet } from "../../../services/genesisApi/httpsMethods";
import { useErrorDialog } from "../../../hooks/useErrorDialog";
import { ErrorDialog } from "../../../components/common/errorDialog";
import { formatDate } from "../../../utils/formatDate";
import { CustomButton } from "../../../components/common/customButton";
import {
  exportDealFunnelToExcel,
  exportFunnelDrillDealsToExcel,
} from "../../../services/summaryExportService";
import { PeriodToggle } from "./PeriodToggle";
import { DealFlowOverview } from "./DealFlowOverview";
import {
  closedApolloMetric,
  nonApolloClosedMetric,
} from "./funnelClosedMetrics";

const GROUP_OPTIONS: { value: FunnelGroupBy; label: string }[] = [
  { value: "overall", label: "Overall" },
  { value: "sector", label: "Sector" },
  { value: "country", label: "Country" },
  { value: "source", label: "Source" },
];

const METRIC_STAGE_KEYS = [
  "received_materials",
  "term_sheet_submitted",
  "reached_signed",
  "reached_closed",
] as const;

const METRIC_OUTCOME_KEYS: (keyof FunnelOutcomes)[] = [
  "lost_to_other_lender",
  "killed_after_term_sheet",
  "killed_after_signing",
];

const DRILL_STAGE_LABELS: Record<FunnelDrillStage, string> = {
  received_materials: "Received Materials",
  term_sheet_submitted: "Term Sheet Submitted",
  reached_signed: "Signed (Still In Pipeline)",
  reached_closed: "Closed (Still Closed)",
  closed_apollo: "Closed - Apollo",
  lost_to_other_lender: "Lost To Other Lender",
  killed_after_term_sheet: "Killed After Term Sheet",
  killed_after_signing: "Killed After Signing",
};

const emptyMetric = (): FunnelMetric => ({ count: 0, volume_gbp: 0 });

/** Defensive defaults when API omits new outcome keys during rollout. */
function outcomeMetric(
  outcomes: FunnelOutcomes,
  key: keyof FunnelOutcomes,
): FunnelMetric {
  const m = outcomes[key];
  return m ?? emptyMetric();
}

function normalizeOutcomes(o: FunnelOutcomes): FunnelOutcomes {
  return {
    lost_to_other_lender: o.lost_to_other_lender ?? emptyMetric(),
    killed_after_term_sheet: o.killed_after_term_sheet ?? emptyMetric(),
    killed_after_signing: o.killed_after_signing ?? emptyMetric(),
  };
}

type MetricMode = "count" | "volume";

type DisplayRow = {
  rowKey: string;
  label: string;
  isTotal: boolean;
  bucketId: number | null | undefined;
  stages: FunnelStages;
  outcomes: FunnelOutcomes;
};

function formatMetricValue(mode: MetricMode, m: FunnelMetric): string {
  if (mode === "count") {
    if (m.count === 0) return "-";
    return m.count.toLocaleString("en-GB");
  }
  if (!Number.isFinite(m.volume_gbp) || m.volume_gbp === 0) {
    return "-";
  }
  const mm = m.volume_gbp / 1_000_000;
  return `£${mm.toLocaleString("en-GB", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}`;
}

function formatRatePercent(rate: number | undefined): string {
  if (rate == null || Number.isNaN(rate)) return "—";
  return `${(rate * 100).toLocaleString("en-GB", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}%`;
}

function receivedSortKey(
  metricMode: MetricMode,
  stages: FunnelStages,
): number {
  const rm = stages.received_materials;
  return metricMode === "count" ? rm.count : rm.volume_gbp;
}

function buildDisplayRows(
  summary: FunnelSummaryResponse | null,
  metricMode: MetricMode,
): DisplayRow[] {
  if (!summary) return [];

  const totalsRow: DisplayRow = {
    rowKey: "total",
    label: "Total",
    isTotal: true,
    bucketId: undefined,
    stages: summary.totals.stages,
    outcomes: normalizeOutcomes(summary.totals.outcomes),
  };

  if (!summary.buckets.length) {
    return [totalsRow];
  }

  // Overall buckets are time periods — keep chronological order. Other group_bys
  // sort by received materials volume/count.
  const sortedBuckets =
    summary.group_by === "overall"
      ? [...summary.buckets].sort((a, b) => {
          if (a.id == null) return 1;
          if (b.id == null) return -1;
          return a.id - b.id;
        })
      : [...summary.buckets].sort(
          (a, b) =>
            receivedSortKey(metricMode, b.stages) -
            receivedSortKey(metricMode, a.stages),
        );

  return [
    totalsRow,
    ...sortedBuckets.map((b: FunnelBucket, i: number) => ({
      rowKey: `bucket-${b.id ?? "unassigned"}-${i}`,
      label: b.name,
      isTotal: false,
      bucketId: b.id,
      stages: b.stages,
      outcomes: normalizeOutcomes(b.outcomes),
    })),
  ];
}

function buildDrillQuery(
  stage: FunnelDrillStage,
  groupBy: FunnelGroupBy,
  row: DisplayRow,
  period: FunnelPeriod,
): Record<string, string | number> {
  const q: Record<string, string | number> = {
    stage,
    group_by: groupBy,
  };
  if (!row.isTotal) {
    if (row.bucketId === null) q.key = "unassigned";
    else if (typeof row.bucketId === "number") q.key = String(row.bucketId);
  }
  if (groupBy === "overall") {
    q.period = period;
  }
  return q;
}

export function DealFunnelTab() {
  const [groupBy, setGroupBy] = useState<FunnelGroupBy>("overall");
  const [metricMode, setMetricMode] = useState<MetricMode>("count");
  const [period, setPeriod] = useState<FunnelPeriod>("quarter");
  const [summary, setSummary] = useState<FunnelSummaryResponse | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillDeals, setDrillDeals] = useState<FunnelDeal[]>([]);
  const [drillTitle, setDrillTitle] = useState("");
  const [isExporting, setIsExporting] = useState(false);

  const { errorDialog, showErrorFromResponse, hideError } = useErrorDialog();
  const isOverall = groupBy === "overall";

  useEffect(() => {
    let cancelled = false;
    setSummaryLoading(true);

    const params: Record<string, string | number> = { group_by: groupBy };
    if (groupBy === "overall") {
      params.period = period;
    }

    apiGet<FunnelSummaryResponse>("deals/funnel/summary", params)
      .then((response) => {
        if (cancelled) return;
        if (response.success && response.data) {
          setSummary(response.data);
        } else {
          showErrorFromResponse(response);
          setSummary(null);
        }
      })
      .catch(() => {
        if (cancelled) return;
        showErrorFromResponse({
          success: false,
          status: 500,
          message: "Failed to load deal funnel summary. Please try again.",
        });
        setSummary(null);
      })
      .finally(() => {
        if (!cancelled) setSummaryLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupBy, period]);

  const rows = useMemo(
    () => buildDisplayRows(summary, metricMode),
    [summary, metricMode],
  );

  // Monotonic id of the latest drill request; responses from earlier clicks
  // are ignored so slice A's deals never render under slice B's title.
  const drillRequestIdRef = useRef(0);

  const openDrill = useCallback(
    (stage: FunnelDrillStage, row: DisplayRow) => {
      const requestId = ++drillRequestIdRef.current;
      const isCurrent = () => drillRequestIdRef.current === requestId;
      const titleParts = [DRILL_STAGE_LABELS[stage]];
      if (!row.isTotal) titleParts.push(row.label);
      else titleParts.push("All buckets");
      setDrillTitle(titleParts.join(" · "));
      setDrawerOpen(true);
      setDrillLoading(true);
      setDrillDeals([]);

      const query = buildDrillQuery(stage, groupBy, row, period);
      apiGet<FunnelDeal[]>("deals/funnel/deals", query)
        .then((response) => {
          if (!isCurrent()) return;
          if (response.success && Array.isArray(response.data)) {
            setDrillDeals(response.data);
          } else {
            showErrorFromResponse(response);
            setDrillDeals([]);
          }
        })
        .catch(() => {
          if (!isCurrent()) return;
          showErrorFromResponse({
            success: false,
            status: 500,
            message: "Failed to load deals for this slice. Please try again.",
          });
          setDrillDeals([]);
        })
        .finally(() => {
          if (isCurrent()) setDrillLoading(false);
        });
    },
    [groupBy, period, showErrorFromResponse],
  );

  const handleExportFunnelToExcel = async () => {
    setIsExporting(true);
    try {
      await exportDealFunnelToExcel();
    } catch (error) {
      alert(
        error instanceof Error
          ? error.message
          : "Failed to export deal funnel. Please try again.",
      );
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportDrillDeals = () => {
    if (drillLoading || drillDeals.length === 0) return;
    setIsExporting(true);
    try {
      exportFunnelDrillDealsToExcel({
        deals: drillDeals,
        sliceTitle: drillTitle,
      });
    } catch (error) {
      showErrorFromResponse({
        success: false,
        status: 500,
        message:
          error instanceof Error
            ? error.message
            : "Failed to export funnel deals. Please try again.",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const totalRowSx = {
    backgroundColor: "#e8eaed",
    color: "#182737",
    "& td": { borderTop: "3px solid #182737", fontWeight: 600 },
  };

  const convCell = (
    rateCount: number | undefined,
    rateVolume: number | undefined,
  ) => {
    const rate = metricMode === "count" ? rateCount : rateVolume;
    const pct =
      rate != null && !Number.isNaN(rate)
        ? Math.max(0, Math.min(100, rate * 100))
        : null;
    return (
      <TableCell align="right" sx={{ verticalAlign: "middle", minWidth: 140 }}>
        {pct == null ? (
          <Typography variant="body2" component="span">
            —
          </Typography>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              justifyContent: "flex-end",
            }}
          >
            <Box
              sx={{
                flex: 1,
                minWidth: 56,
                maxWidth: 120,
                height: 10,
                borderRadius: 0.5,
                bgcolor: "action.hover",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: `${pct}%`,
                  bgcolor: "#1B7A63",
                  borderRadius: 0.5,
                }}
              />
            </Box>
            <Typography
              variant="body2"
              component="span"
              sx={{ minWidth: "3.25rem", textAlign: "right", flexShrink: 0 }}
            >
              {formatRatePercent(rate)}
            </Typography>
          </Box>
        )}
      </TableCell>
    );
  };

  const headerCellSx = {
    whiteSpace: "normal",
    wordBreak: "break-word",
    lineHeight: 1.25,
    verticalAlign: "bottom",
    maxWidth: 150,
    minWidth: 88,
    backgroundColor: "#ffffff",
  } as const;

  return (
    <Box
      sx={{
        pt: 2,
        pb: 4,
        width: "100%",
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        // Grow with content so the page can scroll (table + Deal Flow slide)
        minHeight: "calc(100dvh - 140px)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "center",
          }}
        >
          <ToggleButtonGroup
            exclusive
            size="small"
            value={groupBy}
            onChange={(_, next: FunnelGroupBy | null) => {
              if (next) setGroupBy(next);
            }}
          >
            {GROUP_OPTIONS.map((o) => (
              <ToggleButton key={o.value} value={o.value}>
                {o.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <ToggleButtonGroup
            exclusive
            size="small"
            value={metricMode}
            onChange={(_, next: MetricMode | null) => {
              if (next) setMetricMode(next);
            }}
          >
            <ToggleButton value="count">Counts</ToggleButton>
            <ToggleButton value="volume">Volume (£MM)</ToggleButton>
          </ToggleButtonGroup>

          {isOverall ? (
            <PeriodToggle value={period} onChange={setPeriod} />
          ) : null}
        </Box>

        <CustomButton
          handleButtonPress={handleExportFunnelToExcel}
          isButtonDisabled={isExporting}
          text="Export to Excel"
          isLoading={isExporting}
          isLoadingText="Exporting..."
          solid={false}
          widthOverride="140px"
        />
      </Box>

      {summaryLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer
            sx={{
              flex: isOverall ? "none" : 1,
              maxHeight: isOverall ? 240 : undefined,
              minHeight: isOverall ? 140 : 0,
              overflow: "auto",
              maxWidth: "100%",
              backgroundColor: "#ffffff",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
            }}
          >
            <Table size="small" stickyHeader sx={{ minWidth: 1220 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>Bucket</TableCell>
                  <TableCell align="right" sx={headerCellSx}>
                    Received Materials
                  </TableCell>
                  <TableCell align="right" sx={headerCellSx}>
                    Term Sheet Submitted
                  </TableCell>
                  <TableCell align="right" sx={headerCellSx}>
                    Signed
                  </TableCell>
                  <TableCell align="right" sx={headerCellSx}>
                    Closed
                  </TableCell>
                  <TableCell align="right" sx={headerCellSx}>
                    Closed - Apollo
                  </TableCell>
                  <TableCell align="right" sx={headerCellSx}>
                    Lost To Other Lender
                  </TableCell>
                  <TableCell align="right" sx={headerCellSx}>
                    Killed After Term Sheet
                  </TableCell>
                  <TableCell align="right" sx={headerCellSx}>
                    Killed After Signing
                  </TableCell>
                  <TableCell align="right" sx={headerCellSx}>
                    Term Sheet Submitted %
                  </TableCell>
                  <TableCell align="right" sx={headerCellSx}>
                    Deals Won %
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow
                    key={row.rowKey}
                    sx={row.isTotal ? totalRowSx : undefined}
                  >
                    <TableCell>{row.label}</TableCell>
                    {METRIC_STAGE_KEYS.map((stageKey) => {
                      const metricValue =
                        stageKey === "reached_closed"
                          ? nonApolloClosedMetric(row.stages)
                          : row.stages[stageKey];
                      return (
                        <TableCell key={stageKey} align="right" sx={{ p: 0 }}>
                          <ButtonBase
                            onClick={() => openDrill(stageKey, row)}
                            sx={{
                              width: "100%",
                              justifyContent: "flex-end",
                              px: 2,
                              py: 1,
                              borderRadius: 0,
                              "&:hover": { backgroundColor: "action.hover" },
                            }}
                          >
                            <Typography variant="body2" component="span">
                              {formatMetricValue(metricMode, metricValue)}
                            </Typography>
                          </ButtonBase>
                        </TableCell>
                      );
                    })}
                    <TableCell align="right" sx={{ p: 0 }}>
                      <ButtonBase
                        onClick={() => openDrill("closed_apollo", row)}
                        sx={{
                          width: "100%",
                          justifyContent: "flex-end",
                          px: 2,
                          py: 1,
                          borderRadius: 0,
                          "&:hover": { backgroundColor: "action.hover" },
                        }}
                      >
                        <Typography variant="body2" component="span">
                          {formatMetricValue(
                            metricMode,
                            closedApolloMetric(row.stages),
                          )}
                        </Typography>
                      </ButtonBase>
                    </TableCell>
                    {METRIC_OUTCOME_KEYS.map((outcomeKey) => (
                      <TableCell key={outcomeKey} align="right" sx={{ p: 0 }}>
                        <ButtonBase
                          onClick={() => openDrill(outcomeKey, row)}
                          sx={{
                            width: "100%",
                            justifyContent: "flex-end",
                            px: 2,
                            py: 1,
                            borderRadius: 0,
                            "&:hover": { backgroundColor: "action.hover" },
                          }}
                        >
                          <Typography variant="body2" component="span">
                            {formatMetricValue(
                              metricMode,
                              outcomeMetric(row.outcomes, outcomeKey),
                            )}
                          </Typography>
                        </ButtonBase>
                      </TableCell>
                    ))}
                    {convCell(
                      row.stages.term_sheet_submitted.rate_count_from_received,
                      row.stages.term_sheet_submitted.rate_volume_from_received,
                    )}
                    {convCell(
                      row.stages.reached_signed.rate_count_from_tss,
                      row.stages.reached_signed.rate_volume_from_tss,
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {isOverall && summary?.deal_flow ? (
            <DealFlowOverview dealFlow={summary.deal_flow} />
          ) : null}
        </>
      )}

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 520 },
            maxWidth: "100vw",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1,
            p: 2,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Typography variant="h6" sx={{ pr: 1 }}>
            {drillTitle}
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexShrink: 0,
            }}
          >
            <CustomButton
              handleButtonPress={handleExportDrillDeals}
              isButtonDisabled={
                drillLoading || isExporting || drillDeals.length === 0
              }
              text="Export"
              isLoading={isExporting}
              isLoadingText="Exporting..."
              solid={false}
              widthOverride="88px"
            />
            <IconButton
              aria-label="Close"
              onClick={() => setDrawerOpen(false)}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
        <Box sx={{ p: 2, overflowY: "auto", flex: 1, minHeight: 0 }}>
          {drillLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress size={32} />
            </Box>
          ) : drillDeals.length === 0 ? (
            <Typography color="text.secondary">
              No deals match this slice.
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {drillDeals.map((deal) => (
                <Box
                  key={deal.id}
                  sx={{
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    p: 1.5,
                  }}
                >
                  <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                    <Box
                      component="a"
                      href={`/deals/${deal.id}`}
                      sx={{
                        color: "#1B7A63",
                        textDecoration: "underline",
                        fontWeight: 600,
                      }}
                    >
                      {deal.deal_name?.trim() || `Deal ${deal.id}`}
                    </Box>
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {deal.current_status?.name ?? "No status"}
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1 }}>
                    <Chip size="small" label={deal.primary_country.name} />
                    <Chip size="small" label={deal.primary_sector.name} />
                    {deal.source ? (
                      <Chip size="small" label={deal.source.name} />
                    ) : (
                      <Chip size="small" label="No source" variant="outlined" />
                    )}
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Loan:{" "}
                    {deal.loan_amount_fully_funded != null
                      ? `${deal.loan_amount_fully_funded.toLocaleString("en-GB")}${
                          deal.loan_amount_currency
                            ? ` ${deal.loan_amount_currency}`
                            : ""
                        }`
                      : "—"}
                    {" · "}
                    {new Intl.NumberFormat("en-GB", {
                      style: "currency",
                      currency: "GBP",
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    }).format(deal.loan_amount_gbp)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Created: {formatDate(deal.created_date)}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Drawer>

      <ErrorDialog
        open={errorDialog.open}
        onClose={hideError}
        apiResponse={errorDialog.apiResponse}
        customMessage={errorDialog.customMessage}
        title={errorDialog.title}
      />
    </Box>
  );
}
